import React, { Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useHelp } from "../contexts/HelpContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faQuestionCircle } from "@fortawesome/free-solid-svg-icons";

const getPageTitle = (pathname) => {
  if (pathname.startsWith("/masters/new-simulation-triggers"))
    return "Simulation Triggers";
  if (pathname.startsWith("/masters/maintenance-cycle-limits"))
    return "Maintenance Cycle Limits";
  if (pathname.startsWith("/masters/wash-rules")) return "Wash Rules";
  if (pathname.startsWith("/masters/field-application-labor"))
    return "Field Application Labor";
  if (pathname.startsWith("/masters/labor-by-coat"))
    return "Labor by Coat Type";
  if (pathname.startsWith("/masters/corrosion-damage"))
    return "Corrosion & Damage";
  if (pathname.startsWith("/masters/repair-rules")) return "Repair Rules";
  if (pathname.startsWith("/masters/surface-prep-methods"))
    return "Surface Prep Methods";
  if (pathname.startsWith("/masters/washing-costs")) return "Washing Costs";
  if (pathname.startsWith("/masters/materials-master")) return "Paints Master";
  if (pathname.startsWith("/masters/mobilization-costs"))
    return "Mobilization Costs";
  if (pathname.startsWith("/dashboard")) return "Dashboard";
  if (pathname.startsWith("/bridges")) return "Bridge Management";
  if (pathname.startsWith("/repair-projects")) return "Repair Projects";
  if (pathname.startsWith("/enhanced-simulation-results"))
    return "Simulation Engine";
  if (pathname.startsWith("/profile")) return "Profile";
  if (pathname.startsWith("/users")) return "User Management";
  if (pathname.startsWith("/settings")) return "Settings";
  if (pathname.startsWith("/help")) return "Help & Documentation";
  return "";
};

const Header = ({ user, userNavigation, setSidebarOpen }) => {
  const { logout } = useAuth();
  const location = useLocation();
  const { openHelpForPath } = useHelp();
  const pageTitle = getPageTitle(location.pathname);

  const handleHelpClick = () => {
    const opened = openHelpForPath(location.pathname);
    if (!opened) window.location.href = "/help";
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <header className="sticky top-0 z-30 bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <button
              type="button"
              className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <FontAwesomeIcon icon={faBars} className="h-6 w-6" />
            </button>
            {pageTitle && (
              <h1 className="ml-4 text-xl font-semibold text-gray-900 truncate">
                {pageTitle}
              </h1>
            )}

            <button
              type="button"
              className="ml-4 inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
              onClick={handleHelpClick}
            >
              <FontAwesomeIcon icon={faQuestionCircle} className="h-4 w-4" />
              Help
            </button>
          </div>

          <div className="flex items-center">
            {/* Profile dropdown */}
            <Menu as="div" className="ml-3 relative">
              <div>
                <Menu.Button className="max-w-xs bg-white flex items-center gap-3 text-sm rounded-md px-1 py-1 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 hover:bg-slate-50">
                  <span className="sr-only">Open user menu</span>
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-medium text-gray-900 leading-5">
                      {user?.fullName || user?.username || "User"}
                    </p>
                    <p className="text-xs text-gray-500 capitalize leading-4">
                      {user?.role || user?.bridgebudgetRole || "Role"}
                    </p>
                  </div>
                  <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {user?.fullName?.charAt(0) ||
                        user?.username?.charAt(0) ||
                        "U"}
                    </span>
                  </div>
                </Menu.Button>
              </div>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                  {userNavigation.map((item) => (
                    <Menu.Item key={item.name}>
                      {({ active }) => (
                        <a
                          href={item.href}
                          className={`${
                            active ? "bg-gray-100" : ""
                          } block px-4 py-2 text-sm text-gray-700`}
                        >
                          {item.name}
                        </a>
                      )}
                    </Menu.Item>
                  ))}

                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleLogout}
                        className={`${
                          active ? "bg-gray-100" : ""
                        } block w-full text-left px-4 py-2 text-sm text-gray-700`}
                      >
                        Sign out
                      </button>
                    )}
                  </Menu.Item>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
